/* Skills — teacher-owned repository of practice items organised by subject,
 * grade, topic and difficulty. Feeds the Whiteboard Assets Panel and the
 * upcoming step-by-step student practice mode.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, X, Filter, BookOpen, ClipboardList, Image as ImageIcon, PenTool, CreditCard, Sparkles, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { SubjectPicker, GradePicker } from "@/components/CurriculumPicker";

const ASSET_TYPES = [
  { id: "skill",       label: "Skill",       icon: Sparkles,        tint: "#7C3AED" },
  { id: "worksheet",   label: "Worksheet",   icon: ClipboardList,   tint: "#059669" },
  { id: "diagram",     label: "Diagram",     icon: PenTool,         tint: "#F97316" },
  { id: "image",       label: "Image",       icon: ImageIcon,       tint: "#0EA5E9" },
  { id: "flashcards",  label: "Flashcards",  icon: CreditCard,      tint: "#DC2626" },
  { id: "activity",    label: "Activity",    icon: BookOpen,        tint: "#B45309" },
];

const DIFFICULTIES = ["easy", "medium", "hard"];

export default function SkillsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [assetType, setAssetType] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/skills", {
        params: {
          subject_id: subjectId, grade_id: gradeId,
          asset_type: assetType, difficulty, q,
        },
      });
      setItems(data.skills || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load skills");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [subjectId, gradeId, assetType, difficulty]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const remove = async (id) => {
    if (!confirm("Delete this skill?")) return;
    try { await api.delete(`/skills/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="skills-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ practice library</div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter">Skills</h1>
          <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
            Your searchable, sortable repository of practice items — reused by the whiteboard, live class, and student practice pages.
          </p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="skills-new-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#0A0C10] text-[#FFD600] text-sm font-bold hover:bg-[#0A0C10]/90">
          <Plus size={14} /> New skill
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-black/8 rounded-xl p-4 space-y-3" data-testid="skills-filters">
        <div className="flex items-center gap-2">
          <Search size={14} className="text-neutral-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, topic, description…"
            data-testid="skills-search"
            className="flex-1 bg-transparent border-none outline-none text-sm"
          />
          {q && <button onClick={() => setQ("")}><X size={12} className="text-neutral-400" /></button>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <SubjectPicker value={subjectId} onChange={setSubjectId} testid="skills-subject-filter" placeholder="All subjects" />
          <GradePicker value={gradeId} onChange={setGradeId} testid="skills-grade-filter" placeholder="All grades" includeNone />
          <select value={assetType} onChange={(e) => setAssetType(e.target.value)} data-testid="skills-type-filter"
            className="bg-white border border-black/15 rounded-md px-3 py-2 text-sm outline-none">
            <option value="">All types</option>
            {ASSET_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} data-testid="skills-diff-filter"
            className="bg-white border border-black/15 rounded-md px-3 py-2 text-sm outline-none">
            <option value="">All difficulty</option>
            {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-400 inline-flex items-center gap-1">
          <Filter size={10} /> {items.length} result{items.length === 1 ? "" : "s"}
        </div>
      </div>

      {/* Results grid */}
      {loading ? (
        <div className="text-center text-neutral-500 text-sm py-12">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-neutral-500 text-sm py-16 bg-white border border-black/8 rounded-xl">
          No skills yet — click <strong>New skill</strong> to add one.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((s) => {
            const meta = ASSET_TYPES.find((t) => t.id === s.asset_type) || ASSET_TYPES[0];
            const Icon = meta.icon;
            return (
              <div key={s.id} data-testid={`skill-${s.id}`}
                className="bg-white border border-black/8 rounded-xl p-4 hover:border-[#0A0C10] transition-colors group">
                <div className="flex items-center justify-between">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: `${meta.tint}20`, color: meta.tint }}>
                    <Icon size={14} />
                  </div>
                  <button onClick={() => remove(s.id)} className="text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`skill-delete-${s.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="mt-2 font-display font-semibold text-[15px] text-[#0A0C10] line-clamp-2">{s.title}</div>
                <div className="mt-1 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
                  {meta.label} · {s.difficulty}{s.topic ? ` · ${s.topic}` : ""}
                </div>
                {s.description && (
                  <div className="mt-2 text-xs text-neutral-600 line-clamp-3">{s.description}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showNew && <NewSkillModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewSkillModal({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", topic: "", description: "", subject_id: "", grade_id: "", asset_type: "skill", difficulty: "medium" });
  const [questions, setQuestions] = useState([{ prompt: "", options: ["", "", "", ""], answer_index: 0 }]);
  const [busy, setBusy] = useState(false);

  const updateQ = (i, patch) => setQuestions((qs) => qs.map((q, j) => j === i ? { ...q, ...patch } : q));
  const updateOpt = (i, k, val) => setQuestions((qs) => qs.map((q, j) => j === i ? { ...q, options: q.options.map((o, m) => m === k ? val : o) } : q));
  const addQ = () => setQuestions((qs) => [...qs, { prompt: "", options: ["", "", "", ""], answer_index: 0 }]);
  const removeQ = (i) => setQuestions((qs) => qs.length > 1 ? qs.filter((_, j) => j !== i) : qs);

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Title required");
    const cleaned = questions
      .map((q) => ({ prompt: q.prompt.trim(), options: q.options.map((o) => o.trim()), answer_index: Number(q.answer_index) || 0 }))
      .filter((q) => q.prompt && q.options.some((o) => o));
    setBusy(true);
    try {
      await api.post("/skills", { ...f, payload: { questions: cleaned } });
      toast.success("Skill added");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="skills-new-modal">
      <div className="bg-white w-full max-w-2xl rounded-lg p-6 border border-black/8 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-black/8">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ skills</div>
            <h3 className="font-display font-bold text-xl tracking-tighter mt-0.5">New skill</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-[#0A0C10]"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="space-y-3">
          <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Title *</div>
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Adding fractions with same denominator"
              className="w-full bg-white border border-black/15 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0A0C10]" data-testid="skills-new-title" />
          </label>
          <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Topic (optional)</div>
            <input value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} placeholder="Fractions"
              className="w-full bg-white border border-black/15 rounded-md px-3 py-2 text-sm outline-none" data-testid="skills-new-topic" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Subject</div>
              <SubjectPicker value={f.subject_id} onChange={(sid) => setF({ ...f, subject_id: sid })} testid="skills-new-subject" />
            </label>
            <label className="block">
              <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Grade</div>
              <GradePicker value={f.grade_id} onChange={(gid) => setF({ ...f, grade_id: gid })} testid="skills-new-grade" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Type</div>
              <select value={f.asset_type} onChange={(e) => setF({ ...f, asset_type: e.target.value })}
                className="w-full bg-white border border-black/15 rounded-md px-3 py-2 text-sm outline-none" data-testid="skills-new-type">
                {ASSET_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Difficulty</div>
              <select value={f.difficulty} onChange={(e) => setF({ ...f, difficulty: e.target.value })}
                className="w-full bg-white border border-black/15 rounded-md px-3 py-2 text-sm outline-none" data-testid="skills-new-diff">
                {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Description</div>
            <textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Instructions students see before starting."
              className="w-full bg-white border border-black/15 rounded-md px-3 py-2 text-sm outline-none" data-testid="skills-new-desc" />
          </label>

          {/* Questions builder — feeds the IXL-style practice page */}
          <div className="border-t border-black/8 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">Practice questions</div>
              <button type="button" onClick={addQ} data-testid="skills-add-question"
                className="text-[11px] font-semibold text-[#7C3AED] inline-flex items-center gap-1">
                <Plus size={11} /> Add question
              </button>
            </div>
            <div className="space-y-3">
              {questions.map((q, i) => (
                <div key={i} className="bg-neutral-50 border border-black/6 rounded-md p-3 space-y-2" data-testid={`skills-question-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Q{i + 1}</div>
                    {questions.length > 1 && (
                      <button type="button" onClick={() => removeQ(i)} className="text-red-400 hover:text-red-600 text-[10px] font-semibold">Remove</button>
                    )}
                  </div>
                  <input value={q.prompt} onChange={(e) => updateQ(i, { prompt: e.target.value })} placeholder="Question prompt (e.g. 1/2 + 1/2 = ?)"
                    className="w-full bg-white border border-black/15 rounded-md px-2.5 py-1.5 text-sm outline-none" data-testid={`skills-q-prompt-${i}`} />
                  <div className="grid grid-cols-2 gap-2">
                    {q.options.map((o, k) => (
                      <label key={k} className={`flex items-center gap-2 bg-white border rounded-md px-2 py-1.5 cursor-pointer ${q.answer_index === k ? "border-[#10B981] bg-emerald-50" : "border-black/15"}`}>
                        <input type="radio" name={`q-${i}-ans`} checked={q.answer_index === k} onChange={() => updateQ(i, { answer_index: k })}
                          className="accent-[#10B981]" />
                        <input value={o} onChange={(e) => updateOpt(i, k, e.target.value)} placeholder={`Option ${k + 1}`}
                          className="flex-1 bg-transparent border-none outline-none text-sm" />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="skills-new-save"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-[#0A0C10] text-[#FFD600] font-bold disabled:opacity-50">
            <Plus size={14} /> {busy ? "Saving…" : "Save skill"}
          </button>
        </form>
      </div>
    </div>
  );
}
